// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Admin.js
// Admin panel: users, registrations, site settings.
// Tools links to the DB map, directory map, announcement AI prompts, Discord bot, AI audit, backtest, AI picks,
// logs, job log and Security (bans, allow / deny lists - the old email blacklist lives there now) pages.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  nav: {
    background: 'var(--surface)', borderBottom: '1px solid var(--border)',
    padding: '0 32px', height: '56px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
  },
  logo: {
    fontFamily: "'DM Mono', monospace", fontSize: '13px', fontWeight: 500,
    letterSpacing: '0.1em', color: 'var(--accent)', textTransform: 'uppercase',
  },
  navRight: { display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' },
  navBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)',
    fontSize: '13px', fontFamily: "'DM Sans', sans-serif",
    padding: '6px 12px', borderRadius: 'var(--radius)',
  },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '24px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '32px' },
  section: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'calc(var(--radius) * 2)', padding: '24px', marginBottom: '24px',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 600, marginBottom: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)',
    color: 'var(--text-muted)', fontWeight: 500, fontFamily: "'DM Mono', monospace",
    fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase',
  },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },
  btn: {
    padding: '5px 12px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: 500, fontFamily: "'DM Sans', sans-serif",
  },
  row: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  input: {
    padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    fontSize: '13px', fontFamily: "'DM Sans', sans-serif", background: 'var(--bg)',
    color: 'var(--text)', outline: 'none',
  },
  toggle: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', fontSize: '14px' },
  badge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
    fontSize: '11px', fontWeight: 500, fontFamily: "'DM Mono', monospace",
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  err:  { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  ok:   { color: 'var(--success)', fontSize: '13px', marginTop: '8px' },
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
function Btn({ onClick, color = 'var(--accent)', textColor = '#fff', children, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...s.btn, background: color, color: textColor, opacity: disabled ? 0.5 : 1 }}
    >
      {children}
    </button>
  );
}

function Badge({ status }) {
  const map = {
    pending:  { bg: '#FEF3C7', color: '#92400E' },
    approved: { bg: 'var(--success-dim)', color: 'var(--success)' },
    rejected: { bg: 'var(--error-dim)', color: 'var(--error)' },
  };
  const c = map[status] || { bg: 'var(--border)', color: 'var(--text-muted)' };
  return <span style={{ ...s.badge, background: c.bg, color: c.color }}>{status}</span>;
}

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function Admin() {
  const { user } = useAuth();
  const navigate         = useNavigate();

  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');
  const [msg, setMsg]         = useState('');
  const [loading, setLoading] = useState(true);

  // add-user form
  const [newUser, setNewUser] = useState({ email: '', username: '', password: '', is_admin: false });

  const flash = (m, isErr = false) => {
    if (isErr) { setError(m); setMsg(''); }
    else        { setMsg(m);  setError(''); }
    setTimeout(() => { setMsg(''); setError(''); }, 4000);
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/data');
      setData(r.data);
    } catch {
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // redirect non-admins
  useEffect(() => {
    if (!user?.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const post = async (url, body) => {
    await api.post(url, body);
    await load();
  };

  // ---------------------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------------------
  const handleSetting = async (key, val) => {
    try {
      await post('/admin/settings', { [key]: val });
      flash('Setting saved');
    } catch (e) {
      flash(e.response?.data?.error || 'Failed', true);
    }
  };

  // ---------------------------------------------------------------------------
  // Users
  // ---------------------------------------------------------------------------
  const handleAddUser = async () => {
    try {
      await post('/admin/users/add', newUser);
      setNewUser({ email: '', username: '', password: '', is_admin: false });
      flash('User added');
    } catch (e) {
      flash(e.response?.data?.error || 'Failed', true);
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await post('/admin/users/delete', { user_id: uid });
      flash('User deleted');
    } catch (e) {
      flash(e.response?.data?.error || 'Failed', true);
    }
  };

  const handleToggleAdmin = async (uid, makeAdmin) => {
    try {
      await post('/admin/users/toggle_admin', { user_id: uid, make_admin: makeAdmin });
      flash('Admin status updated');
    } catch (e) {
      flash(e.response?.data?.error || 'Failed', true);
    }
  };

  const handleResendConfirmation = async (uid) => {
    const message = window.prompt('Optional message to include in the confirmation email:', '');
    if (message === null) return;
    try {
      await post('/admin/users/resend-confirmation', { user_id: uid, message });
      flash('Confirmation email sent');
    } catch (e) {
      flash(e.response?.data?.error || 'Failed', true);
    }
  };

  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // Pending
  // ---------------------------------------------------------------------------
  const handleApprove = async (pid) => {
    try {
      await post('/admin/pending/approve', { pending_id: pid });
      flash('Approved — confirmation email sent');
    } catch (e) {
      flash(e.response?.data?.error || 'Failed', true);
    }
  };

  const handleReject = async (pid) => {
    if (!window.confirm('Reject this registration?')) return;
    try {
      await post('/admin/pending/reject', { pending_id: pid });
      flash('Rejected');
    } catch (e) {
      flash(e.response?.data?.error || 'Failed', true);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
      Loading…
    </div>
  );

  const { users = [], pending = [], settings = {} } = data || {};
  const pendingOnly = pending.filter(p => p.status === 'pending');

  return (
    <div style={s.page}>
      <Navbar />

      <div style={s.content}>
        <div style={s.heading}>Admin Panel</div>

        {error && <div style={s.err}>{error}</div>}
        {msg   && <div style={s.ok}>{msg}</div>}

        {/* ---- Tools ---- */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Tools</div>
          <div style={s.row}>
            <Btn onClick={() => navigate('/admin/db-map')} color="var(--accent-dim)" textColor="var(--accent)">DB Map</Btn>
            <Btn onClick={() => navigate('/admin/dir-map')} color="var(--accent-dim)" textColor="var(--accent)">Directory Map</Btn>
            <Btn onClick={() => navigate('/admin/prompts')} color="var(--accent-dim)" textColor="var(--accent)">AI Prompts</Btn>
            <Btn onClick={() => navigate('/admin/discord')} color="var(--accent-dim)" textColor="var(--accent)">Discord Bot</Btn>
            <Btn onClick={() => navigate('/admin/audit')} color="var(--accent-dim)" textColor="var(--accent)">AI Audit</Btn>
            <Btn onClick={() => navigate('/admin/stats')} color="var(--accent-dim)" textColor="var(--accent)">AI Stats</Btn>
            <Btn onClick={() => navigate('/admin/backtest')} color="var(--accent-dim)" textColor="var(--accent)">Backtest</Btn>
            <Btn onClick={() => navigate('/admin/firstn')} color="var(--accent-dim)" textColor="var(--accent)">First N Minutes</Btn>
            <Btn onClick={() => navigate('/admin/smc')} color="var(--accent-dim)" textColor="var(--accent)">SMC Lab</Btn>
            <Btn onClick={() => navigate('/admin/picks')} color="var(--accent-dim)" textColor="var(--accent)">AI Picks</Btn>
            <Btn onClick={() => navigate('/admin/logs')} color="var(--accent-dim)" textColor="var(--accent)">Logs &amp; Reports</Btn>
            <Btn onClick={() => navigate('/admin/joblog')} color="var(--accent-dim)" textColor="var(--accent)">Job Log</Btn>
            <Btn onClick={() => navigate('/admin/security')} color="var(--error-dim)" textColor="var(--error)">Security</Btn>
          </div>
        </div>

        {/* ---- Settings ---- */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Site Settings</div>
          <div style={s.toggle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settings.registrations_enabled === '1'}
                onChange={e => handleSetting('registrations_enabled', e.target.checked)}
              />
              Registrations enabled
            </label>
          </div>
          <div style={s.toggle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settings.admin_approval_required === '1'}
                onChange={e => handleSetting('admin_approval_required', e.target.checked)}
              />
              Admin approval required for new registrations
            </label>
          </div>
        </div>

        {/* ---- Pending Approvals ---- */}
        {pendingOnly.length > 0 && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Pending Approvals ({pendingOnly.length})</div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Submitted</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingOnly.map(p => (
                  <tr key={p.id}>
                    <td style={s.td}>{p.details?.email || '—'}</td>
                    <td style={s.td}>{fmt(p.created_at)}</td>
                    <td style={s.td}>
                      <div style={s.row}>
                        <Btn onClick={() => handleApprove(p.id)} color="var(--success)">Approve</Btn>
                        <Btn onClick={() => handleReject(p.id)} color="var(--error)">Reject</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ---- Users ---- */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Users ({users.length})</div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>ID</th>
                <th style={s.th}>Email</th>
                <th style={s.th}>Username</th>
                <th style={s.th}>Admin</th>
                <th style={s.th}>Confirmed</th>
                <th style={s.th}>Created</th>
                <th style={s.th}>Last Login</th>
                <th style={s.th}>Logins</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={s.td}>{u.id}</td>
                  <td style={s.td}>{u.email}</td>
                  <td style={s.td}>{u.username || '—'}</td>
                  <td style={s.td}>
                    <Badge status={u.is_admin ? 'approved' : 'rejected'} />
                  </td>
                  <td style={s.td}>
                    <Badge status={u.confirmed ? 'approved' : 'pending'} />
                  </td>
                  <td style={s.td}>{fmt(u.created_at)}</td>
                  <td style={s.td}>{fmt(u.last_login)}</td>
                  <td style={s.td}>{u.login_count}</td>
                  <td style={s.td}>
                    <div style={s.row}>
                      {!u.confirmed && (
                        <Btn
                          onClick={() => handleResendConfirmation(u.id)}
                          color="var(--accent-dim)"
                          textColor="var(--accent)"
                        >
                          Resend Link
                        </Btn>
                      )}
                      {u.id !== user?.id && (
                        <>
                          <Btn
                            onClick={() => handleToggleAdmin(u.id, !u.is_admin)}
                            color="var(--accent-dim)"
                            textColor="var(--accent)"
                          >
                            {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                          </Btn>
                          <Btn onClick={() => handleDelete(u.id)} color="var(--error-dim)" textColor="var(--error)">
                            Delete
                          </Btn>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Add user */}
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>Add User</div>
            <div style={s.row}>
              <input
                style={{ ...s.input, width: '200px' }}
                placeholder="Email"
                type="email"
                value={newUser.email}
                onChange={e => setNewUser(f => ({ ...f, email: e.target.value }))}
              />
              <input
                style={{ ...s.input, width: '150px' }}
                placeholder="Username (optional)"
                value={newUser.username}
                onChange={e => setNewUser(f => ({ ...f, username: e.target.value }))}
              />
              <input
                style={{ ...s.input, width: '150px' }}
                placeholder="Password"
                type="password"
                value={newUser.password}
                onChange={e => setNewUser(f => ({ ...f, password: e.target.value }))}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newUser.is_admin}
                  onChange={e => setNewUser(f => ({ ...f, is_admin: e.target.checked }))}
                />
                Admin
              </label>
              <Btn onClick={handleAddUser}>Add</Btn>
            </div>
          </div>
        </div>

        {/* ---- Blacklist: moved to Admin > Security (Lists) ---- */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Blacklist, bans and access rules</div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            Email blacklist, IP and account bans, allow / deny lists and the security event log live on the Security page.
          </div>
          <Btn onClick={() => navigate('/admin/security?tab=lists')} color="var(--error-dim)" textColor="var(--error)">Open Security</Btn>
        </div>

        {/* ---- All Pending (history) ---- */}
        {pending.length > 0 && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Approval History</div>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Submitted</th>
                  <th style={s.th}>Updated</th>
                  <th style={s.th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {pending.map(p => (
                  <tr key={p.id}>
                    <td style={s.td}>{p.details?.email || '—'}</td>
                    <td style={s.td}><Badge status={p.status} /></td>
                    <td style={s.td}>{fmt(p.created_at)}</td>
                    <td style={s.td}>{fmt(p.updated_at)}</td>
                    <td style={s.td}>{p.admin_notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
